import { z } from 'zod';
import { Id } from '../primitives.js';
import { Fdi, Surface } from './dental-chart.js';
import { Kopecks } from './finance.js';

/**
 * Контракты плана лечения.
 *
 * План приходит и уходит целиком — вместе с вариантами и позициями. Дробить
 * его на «создать план», «добавить вариант», «добавить позицию» было бы ближе к
 * таблицам, но дальше от работы: врач составляет план на планшете без сети, и в
 * очередь должна лечь одна операция, применяемая целиком. Три десятка операций
 * с зависимостями между ними дали бы наполовину применённый план — вариант без
 * позиций, который нельзя ни показать пациенту, ни объяснить.
 *
 * Документация модуля: docs/03-modules/04-treatment-plans.md
 */

export const PlanStatus = z.enum([
  'draft',
  'proposed',
  'agreed',
  'in_progress',
  'completed',
  'cancelled',
]);
export type PlanStatus = z.infer<typeof PlanStatus>;

/** Эконом, Стандарт, Премиум — три варианта одного лечения. */
export const PlanTier = z.enum(['economy', 'standard', 'premium']);
export type PlanTier = z.infer<typeof PlanTier>;

/**
 * Количество и скидка идут по проводу в человеческом виде — 1.5 и 7.5, — а не в
 * сотых. В базе они целые: считать деньги дробями нельзя. Перевод делает
 * репозиторий, ровно в одном месте.
 */
const Quantity = z.number().positive().max(999).multipleOf(0.01);
const DiscountPercent = z.number().min(0).max(100).multipleOf(0.01);

export const PlanItem = z.object({
  id: Id,
  tenantId: Id,
  optionId: Id,
  /** Этап лечения — обычно один визит. По нему план разворачивается в записи. */
  stageNumber: z.number().int().min(1).max(50),
  serviceId: Id,
  /** Номер зуба по FDI. Пусто у общих услуг вроде осмотра. */
  fdi: Fdi.nullable(),
  surfaces: z.array(Surface).max(6),
  quantity: Quantity,
  /** Цена из прайса на момент составления. Скопирована, а не связана ссылкой. */
  unitPrice: Kopecks,
  discountPercent: DiscountPercent,
  isWarranty: z.boolean(),
  /** Рассчитано ядром, а не введено руками. */
  amount: Kopecks,
  rowVersion: z.number().int().nonnegative(),
});
export type PlanItem = z.infer<typeof PlanItem>;

export const PlanOption = z.object({
  id: Id,
  tenantId: Id,
  planId: Id,
  tier: PlanTier,
  /** Как вариант называется пациенту: «Стандарт — цирконий». */
  label: z.string().min(1).max(200),
  materialCode: z.string().max(50).nullable(),
  warrantyMonths: z.number().int().min(0).max(600),
  totalAmount: Kopecks,
  rowVersion: z.number().int().nonnegative(),
  items: z.array(PlanItem),
});
export type PlanOption = z.infer<typeof PlanOption>;

export const TreatmentPlan = z.object({
  id: Id,
  tenantId: Id,
  patientId: Id,
  version: z.number().int().min(1),
  status: PlanStatus,
  previousVersionId: Id.nullable(),
  agreedOptionId: Id.nullable(),
  agreedAt: z.iso.datetime().nullable(),
  cancelReason: z.string().max(500).nullable(),
  createdBy: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
  options: z.array(PlanOption),
});
export type TreatmentPlan = z.infer<typeof TreatmentPlan>;

/**
 * Позиция в запросе. Цену клиент присылает ту, которую показал пациенту: если
 * прайс за это время изменился, сервер отвечает `PRICE_CHANGED` и новой суммой,
 * а не подставляет другую цифру молча.
 */
export const PlanItemInput = z.object({
  id: Id,
  stageNumber: z.number().int().min(1).max(50).default(1),
  serviceId: Id,
  /**
   * Номер зуба проверяется перечислением, тем же `Fdi`, что и в паспорте зуба.
   * Свободное целое здесь означало бы, что «зуб 9», набранный в поле плана,
   * доезжает до ограничения в базе: операция обрывает пакет очереди и повторяет
   * это при каждой попытке отправки, а врач видит вечно неотправленную работу
   * вместо понятного «такого зуба нет».
   */
  fdi: Fdi.optional(),
  surfaces: z.array(Surface).max(6).default([]),
  quantity: Quantity.default(1),
  unitPrice: Kopecks,
  discountPercent: DiscountPercent.default(0),
  isWarranty: z.boolean().default(false),
});
export type PlanItemInput = z.infer<typeof PlanItemInput>;

export const PlanOptionInput = z.object({
  id: Id,
  tier: PlanTier,
  label: z.string().min(1).max(200),
  materialCode: z.string().max(50).optional(),
  warrantyMonths: z.number().int().min(0).max(600).default(0),
  items: z.array(PlanItemInput).max(100),
});
export type PlanOptionInput = z.infer<typeof PlanOptionInput>;

export const CreateTreatmentPlanRequest = z.object({
  patientId: Id,
  /**
   * До трёх вариантов: столько помещается в сравнении на планшете, повёрнутом
   * к пациенту. Ограничение интерфейсное, но проверяется здесь — иначе врач
   * составит четыре и увидит их только на экране согласования.
   */
  options: z.array(PlanOptionInput).min(1).max(3),
  /** Какую версию плана этот заменяет. Пусто у первого плана по случаю. */
  previousVersionId: Id.optional(),
});
export type CreateTreatmentPlanRequest = z.infer<typeof CreateTreatmentPlanRequest>;

/** Замена состава черновика целиком: варианты и позиции присылаются как есть. */
export const ComposePlanRequest = z.object({
  options: z.array(PlanOptionInput).min(1).max(3),
});
export type ComposePlanRequest = z.infer<typeof ComposePlanRequest>;

/**
 * Смена состояния плана: предложить, согласовать, отказаться, вернуть в
 * черновик, отметить выполнение.
 */
export const ChangePlanStatusRequest = z.object({
  to: PlanStatus,
  /** Обязателен при согласовании: без него неизвестно, на что согласился пациент. */
  optionId: Id.optional(),
  /** Обязательна при отказе: по ней считают, почему планы не проходят. */
  reason: z.string().max(500).optional(),
});
export type ChangePlanStatusRequest = z.infer<typeof ChangePlanStatusRequest>;

/**
 * Изменение плана в очереди устройства.
 *
 * Как и у визита, изменений два разных вида, а операция в протоколе одна.
 * Различать их по форме тела нельзя: такое различение молча ломается от первого
 * необязательного поля.
 */
export const PlanChange = z.discriminatedUnion('change', [
  ComposePlanRequest.extend({ change: z.literal('compose') }),
  ChangePlanStatusRequest.extend({ change: z.literal('status') }),
]);
export type PlanChange = z.infer<typeof PlanChange>;

export const PatientPlansQuery = z.object({
  /** По умолчанию отменённые не показываются: их место в истории, а не в карте. */
  includeCancelled: z.coerce.boolean().default(false),
});
export type PatientPlansQuery = z.infer<typeof PatientPlansQuery>;

export const TreatmentPlans = z.object({
  plans: z.array(TreatmentPlan),
});
export type TreatmentPlans = z.infer<typeof TreatmentPlans>;

/**
 * Что сервер отвечает, когда прайс изменился: новая цена по каждой спорной
 * услуге и новый итог варианта. Этого хватает, чтобы показать врачу «было —
 * стало» и пересчитать план в одно нажатие.
 */
export const PriceMismatch = z.object({
  options: z.array(z.object({ optionId: Id, was: Kopecks, now: Kopecks })),
  services: z.array(z.object({ serviceId: Id, name: z.string(), was: Kopecks, now: Kopecks })),
});
export type PriceMismatch = z.infer<typeof PriceMismatch>;

/**
 * Предел скидки для старшего администратора: право `plan:discount` отвечает на
 * вопрос «может ли эта роль вообще ставить скидку», а это число — на вопрос
 * «сколько именно». Владельца предел не касается никогда — только он его и
 * задаёт.
 *
 * `null` — лимита нет. Это отличается от отсутствия карточки клиники: здесь
 * запись есть с самого первого решения владельца, даже решения «снять лимит»,
 * и видно, кто и когда это сделал.
 */
export const DiscountPolicy = z.object({
  tenantId: Id,
  maxAdminDiscountPercent: DiscountPercent.nullable(),
  updatedBy: Id,
  updatedAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type DiscountPolicy = z.infer<typeof DiscountPolicy>;

export const UpdateDiscountPolicyRequest = z.object({
  maxAdminDiscountPercent: DiscountPercent.nullable(),
});
export type UpdateDiscountPolicyRequest = z.infer<typeof UpdateDiscountPolicyRequest>;
